import { type ReactNode, useId } from "react";
import { appCount } from "../src/app-dates.ts";

/**
 * Where this page sits in the whole list, and the way to the previous and next. The inbox pages by
 * 50 on the server; Mailboxes pages in the browser and lets the reader choose the size (`sizes`).
 * Without `sizes` a list that fits one page shows no pager; with them, one that fits the smallest.
 */
export function Pager<Size extends number>(props: {
  offset: number;
  shown: number;
  total: number;
  pageSize: number;
  onPage: (offset: number) => void;
  /** What the rows are, for screen readers: "Conversations 51 to 100 of 353". */
  noun: string;
  /** Replaces the visible range, for a list that says more than its range (Mailboxes' count line). */
  caption?: ReactNode;
  sizes?: { readonly options: readonly Size[]; readonly onSize: (size: Size) => void };
}) {
  const sizeId = useId();
  const fits = props.total <= Math.min(props.pageSize, ...(props.sizes?.options ?? []));
  if (fits && props.offset === 0) return null;
  const from = props.total === 0 ? 0 : props.offset + 1;
  const to = props.offset + props.shown;
  const sizes = props.sizes;
  return (
    <nav className="lcr-list-caption lcr-pager" aria-label="Pages">
      {props.caption ?? (
        <span className="lcr-pager-range">
          <span aria-hidden="true">
            {appCount(from)}–{appCount(to)} of {appCount(props.total)}
          </span>
          <span className="lcr-visually-hidden">
            {props.noun} {appCount(from)} to {appCount(to)} of {appCount(props.total)}
          </span>
        </span>
      )}
      {sizes && (
        <label className="lcr-pager-size" htmlFor={sizeId}>
          <span>Per page</span>
          <span className="lcr-select">
            <select
              id={sizeId}
              value={props.pageSize}
              onChange={(event) => {
                const next = sizes.options.find((option) => String(option) === event.target.value);
                if (next !== undefined) sizes.onSize(next);
              }}
            >
              {sizes.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </span>
        </label>
      )}
      <button
        type="button"
        className="lcr-small-button"
        disabled={props.offset === 0}
        onClick={() => props.onPage(Math.max(0, props.offset - props.pageSize))}
      >
        Previous
      </button>
      <button
        type="button"
        className="lcr-small-button"
        disabled={to >= props.total}
        onClick={() => props.onPage(props.offset + props.pageSize)}
      >
        Next
      </button>
    </nav>
  );
}
